import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useWebSocket } from '@/hooks/useWebSocket';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('destockpro_token'));
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const ws = useWebSocket(token);

  const loadUser = useCallback(async () => {
    if (!token) {
      // No in-memory token — try cookie-based auth via /auth/me
      try {
        const { data } = await api.get('/auth/me');
        setUser(data);
        setLoading(false);
        return;
      } catch {
        setLoading(false);
        return;
      }
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      localStorage.removeItem('destockpro_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Fetch unread count on login
  const fetchUnreadCount = useCallback(async () => {
    if (!token && !user) { setUnreadCount(0); return; }
    try {
      const { data } = await api.get('/messages/unread-count');
      setUnreadCount(data.unread_count);
    } catch (err) { console.error('Failed to fetch unread count:', err); }
  }, [token, user]);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Listen for WebSocket messages to update unread count
  useEffect(() => {
    const cleanup = ws.addListener((data) => {
      if (data.type === 'new_message') {
        setUnreadCount(prev => prev + 1);
      }
      if (data.type === 'messages_read') {
        fetchUnreadCount();
      }
    });
    return cleanup;
  }, [ws, fetchUnreadCount]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    // Cookie is set by backend httpOnly. Keep token in memory + localStorage for WebSocket.
    localStorage.setItem('destockpro_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const register = async (formData) => {
    const { data } = await api.post('/auth/register', formData);
    if (data.token) {
      localStorage.setItem('destockpro_token', data.token);
      setToken(data.token);
      setUser(data.user);
    }
    return data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('destockpro_token');
    setToken(null);
    setUser(null);
    setUnreadCount(0);
  };

  const updateProfile = async (profileData) => {
    const { data } = await api.put('/auth/profile', profileData);
    setUser(data);
    return data;
  };

  const refreshUser = useCallback(async () => {
    if (!token && !user) return;
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch (err) { console.error('Failed to refresh user:', err); }
  }, [token, user]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateProfile, refreshUser, ws, unreadCount, setUnreadCount, fetchUnreadCount }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
